import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Linkedin, Twitter, Instagram, Mail, ArrowUpRight } from 'lucide-react';
import { Button } from './ui/button';
import LanguageSelector from './LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../i18n/translations';

const navLinksData = [
  { key: 'home', path: '/' },
  { key: 'solution', path: '/solution' },
  { key: 'sports', path: '/sports' },
  { key: 'about', path: '/a-propos' },
  { key: 'contact', path: '/contact' },
];

const navNames = {
  home: { fr: 'Accueil', eu: 'Hasiera', en: 'Home', es: 'Inicio', it: 'Home', ja: 'ホーム', de: 'Start', ru: 'Главная', ka: 'მთავარი' },
  solution: { fr: 'Solution', eu: 'Irtenbidea', en: 'Solution', es: 'Solución', it: 'Soluzione', ja: 'ソリューション', de: 'Lösung', ru: 'Решение', ka: 'გადაწყვეტა' },
  sports: { fr: 'Sports', eu: 'Kirolak', en: 'Sports', es: 'Deportes', it: 'Sport', ja: 'スポーツ', de: 'Sportarten', ru: 'Спорт', ka: 'სპორტი' },
  about: { fr: 'À propos', eu: 'Guri buruz', en: 'About', es: 'Nosotros', it: 'Chi siamo', ja: '会社概要', de: 'Über uns', ru: 'О нас', ka: 'ჩვენ შესახებ' },
  contact: { fr: 'Contact', eu: 'Kontaktua', en: 'Contact', es: 'Contacto', it: 'Contatto', ja: 'お問い合わせ', de: 'Kontakt', ru: 'Контакт', ka: 'კონტაქტი' },
};

const waitlistText = {
  fr: "Liste d'attente",
  eu: "Itxaron zerrenda",
  en: "Waitlist",
  es: "Lista de espera",
  it: "Lista d'attesa",
  ja: "ウェイトリスト",
  de: "Warteliste",
  ru: "Лист ожидания",
  ka: "მოცდის სია"
};

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { language } = useLanguage();

  const navLinks = navLinksData.map(link => ({
    name: navNames[link.key][language] || navNames[link.key]['fr'],
    path: link.path,
    key: link.key
  }));

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-background/80 backdrop-blur-xl border-b border-white/5' : ''}`}>
      <nav className="container-asp">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            {/* Placeholder logo navbar - à remplacer */}
            <div className="h-10 px-4 rounded bg-white/10 border border-white/20 flex items-center justify-center">
              <span className="text-lg font-bold text-white/60">ASP</span>
            </div>
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.key}`}
                className={`text-sm font-medium transition-colors relative ${
                  location.pathname === link.path ? 'text-white' : 'text-white/60 hover:text-white'
                }`}
              >
                {link.name}
                {location.pathname === link.path && (
                  <motion.div layoutId="nav-indicator" className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full" />
                )}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <LanguageSelector />
            <Link to="/contact">
              <Button className="btn-primary group" data-testid="nav-demo-btn">
                {waitlistText[language] || waitlistText['fr']}
                <ArrowUpRight className="ml-2 w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Button>
            </Link>
          </div>

          <button onClick={() => setIsOpen(!isOpen)} className="lg:hidden p-2 text-white" data-testid="mobile-menu-btn" aria-label="Menu">
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {isOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="lg:hidden bg-background/95 backdrop-blur-xl border-t border-white/5">
            <div className="container-asp py-6 space-y-2">
              {navLinks.map((link) => (
                <Link key={link.path} to={link.path} data-testid={`mobile-nav-${link.key}`} className={`block py-3 px-4 rounded-xl font-medium transition-colors ${location.pathname === link.path ? 'bg-primary/20 text-primary' : 'text-white/70 hover:text-white hover:bg-white/5'}`}>
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 flex justify-center">
                <LanguageSelector />
              </div>
              <Link to="/contact" className="block pt-4">
                <Button className="btn-primary w-full" data-testid="mobile-demo-btn">{waitlistText[language] || waitlistText['fr']}</Button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const footerTexts = {
  desc: {
    fr: "Technologie IA propriétaire pour l'entraînement audio-cognitif. Une approche exclusive réservée aux clubs d'élite.",
    eu: "AI teknologia propioa entrenamendu audio-kognitiborako. Eliteko klubetarako gordetako ikuspegi esklusiboa.",
    en: "Proprietary AI technology for audio-cognitive training. An exclusive approach reserved for elite clubs.",
    es: "Tecnología IA propietaria para el entrenamiento audio-cognitivo. Un enfoque exclusivo reservado para clubes de élite.",
    it: "Tecnologia IA proprietaria per l'allenamento audio-cognitivo. Un approccio esclusivo riservato ai club d'élite.",
    ja: "オーディオ認知トレーニングのための独自AI技術。エリートクラブ限定のアプローチ。",
    de: "Proprietäre KI-Technologie für audio-kognitives Training. Ein exklusiver Ansatz für Elite-Clubs.",
    ru: "Собственная технология ИИ для аудио-когнитивных тренировок. Эксклюзивный подход для элитных клубов.",
    ka: "საკუთარი AI ტექნოლოგია აუდიო-კოგნიტური ვარჯიშისთვის. ექსკლუზიური მიდგომა ელიტარული კლუბებისთვის."
  },
  navigation: { fr: 'Navigation', eu: 'Nabigazioa', en: 'Navigation', es: 'Navegación', it: 'Navigazione', ja: 'ナビゲーション', de: 'Navigation', ru: 'Навигация', ka: 'ნავიგაცია' },
  contactTitle: { fr: 'Contact', eu: 'Kontaktua', en: 'Contact', es: 'Contacto', it: 'Contatto', ja: 'お問い合わせ', de: 'Kontakt', ru: 'Контакт', ka: 'კონტაქტი' },
  rights: { fr: 'Tous droits réservés.', eu: 'Eskubide guztiak erreserbatuak.', en: 'All rights reserved.', es: 'Todos los derechos reservados.', it: 'Tutti i diritti riservati.', ja: '全著作権所有。', de: 'Alle Rechte vorbehalten.', ru: 'Все права защищены.', ka: 'ყველა უფლება დაცულია.' },
  legal: { fr: 'Mentions légales', eu: 'Lege oharrak', en: 'Legal notice', es: 'Aviso legal', it: 'Note legali', ja: '法的通知', de: 'Impressum', ru: 'Юридическая информация', ka: 'იურიდიული შენიშვნა' },
  privacy: { fr: 'Confidentialité', eu: 'Pribatutasuna', en: 'Privacy', es: 'Privacidad', it: 'Privacy', ja: 'プライバシー', de: 'Datenschutz', ru: 'Конфиденциальность', ka: 'კონფიდენციალურობა' },
};

export const Footer = () => {
  const { language } = useLanguage();
  
  const navLinks = navLinksData.map(link => ({
    name: navNames[link.key][language] || navNames[link.key]['fr'],
    path: link.path,
    key: link.key
  }));

  return (
    <footer className="bg-background border-t border-white/5">
      <div className="container-asp py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          <div className="md:col-span-2 space-y-6">
            <div className="flex items-center gap-3">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/047c6b1a-f5aa-4ae4-bf18-346561bdb16f/images/fb594f8c5d3030204acb6356a1349a4ba9a3f4220d07814460671c46ffd3644d.png" 
                alt="ASP Logo" 
                className="h-14 w-auto"
              />
            </div>
            <p className="text-white/50 text-sm leading-relaxed max-w-sm">
              {footerTexts.desc[language] || footerTexts.desc['fr']}
            </p>
            <div className="flex gap-4">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/50 hover:text-primary hover:bg-primary/10 transition-all" aria-label="LinkedIn" data-testid="footer-linkedin"><Linkedin size={18} /></a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/50 hover:text-primary hover:bg-primary/10 transition-all" aria-label="Twitter" data-testid="footer-twitter"><Twitter size={18} /></a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-white/50 hover:text-primary hover:bg-primary/10 transition-all" aria-label="Instagram" data-testid="footer-instagram"><Instagram size={18} /></a>
            </div>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-6">{footerTexts.navigation[language] || footerTexts.navigation['fr']}</h4>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-white/50 hover:text-white transition-colors text-sm" data-testid={`footer-${link.key}`}>{link.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-semibold text-white mb-6">{footerTexts.contactTitle[language] || footerTexts.contactTitle['fr']}</h4>
            <ul className="space-y-3">
              <li><a href="mailto:contact@audiosportsperformance.com" className="text-white/50 hover:text-white transition-colors text-sm flex items-center gap-2"><Mail size={14} />contact@asp.com</a></li>
              <li className="text-white/50 text-sm">Bordeaux, France</li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/30 text-sm">© {new Date().getFullYear()} Audio Sports Performance. {footerTexts.rights[language] || footerTexts.rights['fr']}</p>
          <div className="flex gap-6 text-sm">
            <Link to="/mentions-legales" className="text-white/30 hover:text-white/60 transition-colors" data-testid="footer-legal">{footerTexts.legal[language] || footerTexts.legal['fr']}</Link>
            <Link to="/confidentialite" className="text-white/30 hover:text-white/60 transition-colors" data-testid="footer-privacy">{footerTexts.privacy[language] || footerTexts.privacy['fr']}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};

export default Layout;
